import { Fragment, useState } from 'react';
import api from '../../../services/api';
import { ParticipantCell, splitCodesByStatus } from '../shareCodesHelpers';
import { KeyRound, ChevronDown, ChevronRight, Check, X, Copy, Link2, Send, Loader2, Lock, Unlock, ArrowRightLeft } from 'lucide-react';

// Liste des codes quiz mono-sujet, groupés par sujet puis par statut.
// Autonome pour copie / label / invitation / mode / scellés (onReload) ;
// le parent garde le changement de mode (optimiste) et la réaffectation.
export default function MonoCodesList({ quizCodes, onReload, onUpdateMode, onReassign }) {
    const [expandedSs, setExpandedSs] = useState(new Set());
    const [copied, setCopied] = useState(null);
    const [editLabelId, setEditLabelId] = useState(null);
    const [editLabelValue, setEditLabelValue] = useState('');
    const [inviteId, setInviteId] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteSending, setInviteSending] = useState(false);
    const [inviteMsg, setInviteMsg] = useState(null);

    const handleCopy = (code) => {
        navigator.clipboard.writeText(code);
        setCopied(code);
        setTimeout(() => setCopied(null), 2000);
    };
    const handleCopyLink = (code) => {
        const url = `${window.location.origin}/quiz-code?code=${encodeURIComponent(code)}`;
        navigator.clipboard.writeText(url);
        setCopied('link:' + code);
        setTimeout(() => setCopied(null), 2000);
    };

    const handleUpdateLabel = async (codeId) => {
        try {
            await api.post('/quiz-code/list.php', { action: 'update_label', code_id: codeId, label: editLabelValue });
            setEditLabelId(null);
            onReload();
        } catch {}
    };

    const handleInvite = async (codeId) => {
        if (!inviteEmail || !inviteEmail.includes('@')) return;
        setInviteSending(true);
        setInviteMsg(null);
        try {
            const { data } = await api.post('/quiz-code/invite.php', { code_id: codeId, email: inviteEmail });
            if (data.success) {
                setInviteMsg({ type: 'success', text: data.data.message });
                setInviteId(null);
                setInviteEmail('');
                onReload();
            }
        } catch (err) {
            setInviteMsg({ type: 'error', text: err.response?.data?.message || 'Erreur' });
        }
        setInviteSending(false);
    };

    const handleToggleSeal = async (codeId, currentlyReleased) => {
        try {
            const action = currentlyReleased ? 'seal_review' : 'release_review';
            await api.post('/quiz-code/list.php', { action, code_id: codeId });
            onReload();
        } catch (err) {
            alert(err.response?.data?.message || 'Erreur');
        }
    };

    // Group by sous-sujet
    const bySs = {};
    quizCodes.forEach(c => {
        const key = c.sous_sujet_id;
        if (!bySs[key]) bySs[key] = { label: c.sous_sujet_label, codes: [] };
        bySs[key].codes.push(c);
    });

    return (
        <>
        <h4 style={{ marginBottom: 10, fontSize: '0.9em', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--heading)' }}>
            <KeyRound size={15} /> Codes quiz mono-sujet ({quizCodes.length})
        </h4>
        {quizCodes.length === 0 ? (
            <p style={{ fontSize: '0.85em', color: 'var(--text-muted)', fontStyle: 'italic', margin: '0 0 16px' }}>Aucun code existant.</p>
        ) : (<>
        {Object.entries(bySs).map(([ssId, group]) => {
            const isOpen = expandedSs.has(ssId);
            const avail = group.codes.filter(c => c.status === 'AVAILABLE').length;
            const done = group.codes.filter(c => c.status === 'FINISHED').length;
            return (
                <div key={ssId} style={{ marginBottom: 6 }}>
                    <button onClick={() => setExpandedSs(prev => { const n = new Set(prev); n.has(ssId) ? n.delete(ssId) : n.add(ssId); return n; })}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--card-border)', borderRadius: 8, cursor: 'pointer', fontSize: '0.85em', color: 'var(--heading)', fontWeight: 600, textAlign: 'left' }}>
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span style={{ flex: 1 }}>{group.label}</span>
                        <span style={{ fontSize: '0.82em', fontWeight: 400, color: 'var(--text-muted)' }}>
                            {group.codes.length} code{group.codes.length > 1 ? 's' : ''}
                            {avail > 0 && <span style={{ color: 'var(--success)', marginLeft: 6 }}>{avail} dispo</span>}
                            {done > 0 && <span style={{ color: 'var(--primary)', marginLeft: 6 }}>{done} termin&eacute;{done > 1 ? 's' : ''}</span>}
                        </span>
                    </button>
                    {isOpen && (() => {
                        const split = splitCodesByStatus(group.codes);
                        const sections = [
                            { key: 'active',   label: 'Disponibles / en cours',     codes: split.active },
                            { key: 'finished', label: 'Terminés (plus récent en premier)', codes: split.finished },
                            { key: 'archived', label: 'Expirés / révoqués',        codes: split.archived },
                        ].filter(s => s.codes.length > 0);
                        return (
                    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 420, marginTop: 4, border: '1px solid var(--border)', borderRadius: 6 }}>
                        <table className="share-panel__table">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Groupe</th>
                                    <th>Mode</th>
                                    <th>Statut</th>
                                    <th>Participant / Invit&eacute;</th>
                                    <th>Score</th>
                                    <th>Dates</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sections.map((section, si) => (
                                <Fragment key={section.key}>
                                    <tr className="share-panel__section-row">
                                        <td colSpan={8} style={{ background: 'var(--bg-subtle, #f8fafc)', fontSize: '0.75em', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.3, textTransform: 'uppercase', padding: '4px 10px', borderTop: si > 0 ? '2px solid var(--border)' : undefined }}>
                                            {section.label} ({section.codes.length})
                                        </td>
                                    </tr>
                                {section.codes.map(c => (
                                    <tr key={c.id} className={c.status === 'FINISHED' ? 'share-panel__row--muted' : ''}>
                                        <td><code style={{ fontFamily: 'monospace', fontSize: '0.82em', letterSpacing: 1 }}>{c.code}</code></td>
                                        <td>
                                            {editLabelId === c.id ? (
                                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                    <input type="text" value={editLabelValue} onChange={e => setEditLabelValue(e.target.value)}
                                                        placeholder="Groupe..."
                                                        onKeyDown={e => { if (e.key === 'Enter') handleUpdateLabel(c.id); if (e.key === 'Escape') setEditLabelId(null); }}
                                                        style={{ width: 110, padding: '3px 6px', fontSize: '0.82em', borderRadius: 4, border: '1px solid var(--border)' }}
                                                        autoFocus />
                                                    <button className="share-panel__code-copy" onClick={() => handleUpdateLabel(c.id)}><Check size={12} /></button>
                                                    <button className="share-panel__code-copy" onClick={() => setEditLabelId(null)}><X size={12} /></button>
                                                </div>
                                            ) : (
                                                <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                    onClick={() => { setEditLabelId(c.id); setEditLabelValue(c.label || ''); }}>
                                                    {c.label
                                                        ? <span style={{ fontSize: '0.82em', padding: '2px 8px', borderRadius: 4, background: 'rgba(124,58,237,0.1)', color: 'var(--primary)', fontWeight: 500 }}>{c.label}</span>
                                                        : <span style={{ fontSize: '0.78em', opacity: 0.4, whiteSpace: 'nowrap' }}>+ groupe</span>
                                                    }
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {c.status === 'AVAILABLE' ? (
                                                <select value={c.mode || 'quiz'}
                                                    onChange={e => onUpdateMode(c.id, e.target.value)}
                                                    style={{ fontSize: '0.78em', padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--card-bg)', cursor: 'pointer' }}>
                                                    <option value="quiz">Classique</option>
                                                    <option value="apprentissage">Apprentissage</option>
                                                    <option value="sealed">Scellé</option>
                                                </select>
                                            ) : (
                                                <span style={{ fontSize: '0.78em', color: 'var(--text-muted)' }}>
                                                    {c.mode === 'apprentissage' ? 'Apprentissage' : c.mode === 'sealed' ? <><Lock size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} /> Scellé</> : 'Classique'}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`codes-badge codes-badge--${c.status === 'AVAILABLE' ? 'dispo' : c.status === 'IN_PROGRESS' ? 'progress' : c.status === 'FINISHED' ? 'done' : 'dispo'}`}>
                                                {c.status === 'AVAILABLE' ? 'Dispo' : c.status === 'IN_PROGRESS' ? 'En cours' : c.status === 'FINISHED' ? 'Terminé' : c.status}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '0.82em' }}>
                                            <ParticipantCell c={c} />
                                        </td>
                                        <td style={{ fontSize: '0.85em', fontWeight: 600, textAlign: 'right' }}>
                                            {c.status === 'FINISHED' && c.total ? `${c.score}/${c.total}` : <span style={{ opacity: 0.3 }}>&mdash;</span>}
                                        </td>
                                        <td style={{ fontSize: '0.78em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                            {c.started_at || c.finished_at ? (
                                                <>
                                                    {c.started_at ? new Date(c.started_at).toLocaleDateString('fr-FR') : '—'}
                                                    {' → '}
                                                    {c.finished_at ? new Date(c.finished_at).toLocaleDateString('fr-FR') : '…'}
                                                </>
                                            ) : <span style={{ opacity: 0.3 }}>&mdash;</span>}
                                        </td>
                                        <td>
                                            {c.status === 'AVAILABLE' && (
                                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                    <button className="share-panel__code-copy" onClick={() => handleCopy(c.code)} title="Copier le code">
                                                        {copied === c.code ? <Check size={12} /> : <Copy size={12} />}
                                                    </button>
                                                    <button className="share-panel__code-copy" onClick={() => handleCopyLink(c.code)} title="Copier le lien participant">
                                                        {copied === 'link:' + c.code ? <Check size={12} /> : <Link2 size={12} />}
                                                    </button>
                                                    <button className="share-panel__code-copy"
                                                        onClick={() => { setInviteId(inviteId === c.id ? null : c.id); setInviteEmail(c.email_invitation || ''); setInviteMsg(null); }}
                                                        title={c.email_invitation ? `Invitation envoyée à ${c.email_invitation}` : 'Envoyer par email'}>
                                                        <Send size={12} />
                                                    </button>
                                                    <button className="share-panel__code-copy"
                                                        onClick={() => onReassign(c)}
                                                        title="Réaffecter ce code à un autre sujet">
                                                        <ArrowRightLeft size={12} />
                                                    </button>
                                                </div>
                                            )}
                                            {c.status === 'FINISHED' && c.mode === 'sealed' && (
                                                <button className="share-panel__code-copy"
                                                    onClick={() => handleToggleSeal(c.id, !!c.review_released_at)}
                                                    title={c.review_released_at ? 'Rescellé les résultats' : 'Desceller les résultats (le participant pourra les consulter)'}
                                                    style={{ color: c.review_released_at ? '#10B981' : '#F59E0B' }}>
                                                    {c.review_released_at ? <Unlock size={12} /> : <Lock size={12} />}
                                                </button>
                                            )}
                                            {inviteId === c.id && (
                                                <div className="share-panel__invite-form" style={{ marginTop: 6 }}>
                                                    <input type="email"
                                                        value={inviteEmail}
                                                        onChange={e => setInviteEmail(e.target.value)}
                                                        placeholder="email@exemple.fr"
                                                        onKeyDown={e => e.key === 'Enter' && handleInvite(c.id)} />
                                                    <button className="btn btn-xs btn-primary"
                                                        onClick={() => handleInvite(c.id)}
                                                        disabled={!inviteEmail || inviteSending}>
                                                        {inviteSending ? <Loader2 size={11} className="spin" /> : <Send size={11} />} Envoyer
                                                    </button>
                                                    <button className="btn btn-xs btn-ghost" onClick={() => setInviteId(null)}>
                                                        <X size={11} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                        );
                    })()}
                </div>
            );
        })}
        {inviteMsg && (
            <div className={`share-panel__invite-msg share-panel__invite-msg--${inviteMsg.type}`}>
                {inviteMsg.text}
            </div>
        )}
        </>)}
        </>
    );
}
